import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  memo,
} from "react";
import { useTranslation } from "react-i18next";
import styles from "./styles.module.scss";
import CalendarToolbar from "./components/CalendarToolbar";
import CalendarEvent from "./components/CalendarEvent";
import AddNewBookingModal from "modals/AddNewBookingModal";
import { useModal } from "context";
import { useLanguage } from "context";
import createPrivateBookingService from "services/CreatePrivateBookingService";
import CalendarService from "services/CalendarService";
import { useSelector } from "react-redux";
import { useOutletContext } from "react-router";
import CalendarEventDetailsModal from "modals/GoogleEventModal";
import PrivateBookingDetailsModal from "modals/PrivateBookingDetailsModal";
import CustomBookingDetailsModal from "modals/CustomBookingDetailsModal";
import { CalendarIcon } from "./components/Icons";
import AddNewBookingModal2 from "modals/AddNewBookingModal2";

// Constants
const EVENT_TYPES = {
  BOOKING: "booking",
  PRIVATE: "private",
  GOOGLE: "google",
};

const DEFAULT_COLORS = {
  [EVENT_TYPES.BOOKING]: "#4285F4",
  [EVENT_TYPES.PRIVATE]: "#FBBC04",
  [EVENT_TYPES.GOOGLE]: "#34A853",
};

const VIEWS = {
  DAY: "day",
  WEEK: "week",
  MONTH: "month",
  AGENDA: "agenda",
};

// Time constants
const WORKING_DAY_START_HOUR = 8; // 8:00 AM
const WORKING_DAY_END_HOUR = 20; // 8:00 PM
const TOTAL_WORKING_HOURS = WORKING_DAY_END_HOUR - WORKING_DAY_START_HOUR; // 12 hours
const MINUTES_PER_HOUR = 60;
const TOTAL_WORKING_MINUTES = TOTAL_WORKING_HOURS * MINUTES_PER_HOUR; // 720 minutes
const WORKING_DAY_START_MINUTES = WORKING_DAY_START_HOUR * MINUTES_PER_HOUR; // 480 minutes
const WORKING_DAY_END_MINUTES = WORKING_DAY_END_HOUR * MINUTES_PER_HOUR; // 1200 minutes

// Display constants
const HOUR_SLOT_HEIGHT = 60; // pixels per hour slot
const TOTAL_VISIBLE_HEIGHT = TOTAL_WORKING_HOURS * HOUR_SLOT_HEIGHT; // 720px
const PIXELS_PER_MINUTE = TOTAL_VISIBLE_HEIGHT / TOTAL_WORKING_MINUTES; // 1px per minute

// Working hours for display
const WORKING_HOURS = Array.from(
  { length: TOTAL_WORKING_HOURS },
  (_, i) => i + WORKING_DAY_START_HOUR
); // 8 to 19 (12 hours)

// Default booking duration (in minutes)
const DEFAULT_BOOKING_DURATION = 60; // 1 hour

// Utility Functions
const formatTime = (date) => {
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  return `${hours}:${minutes}`;
};

const isSameDay = (date1, date2) => {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};

const isMultiDayEvent = (start, end) => {
  const startDay = new Date(start);
  const endDay = new Date(end);
  startDay.setHours(0, 0, 0, 0);
  endDay.setHours(0, 0, 0, 0);
  return startDay.getTime() !== endDay.getTime();
};

const getMinutesFromMidnight = (date) => {
  return date.getHours() * MINUTES_PER_HOUR + date.getMinutes();
};

const getEventDisplayInfo = (event, day) => {
  const eventStart = new Date(event.start);
  const eventEnd = new Date(event.end);
  const dayStart = new Date(day);
  dayStart.setHours(0, 0, 0, 0);
  const dayEnd = new Date(day);
  dayEnd.setHours(23, 59, 59, 999);

  if (eventStart >= dayEnd || eventEnd <= dayStart) {
    return null;
  }

  const spansFromPrevious = eventStart < dayStart;
  const spansToNext = eventEnd > dayEnd;
  const isMultiDay =
    spansFromPrevious || spansToNext || isMultiDayEvent(eventStart, eventEnd);

  // Use the actual event times for display
  let displayStart, displayEnd;

  if (spansFromPrevious) {
    displayStart = "00:00";
  } else {
    displayStart = formatTime(eventStart);
  }

  if (spansToNext) {
    displayEnd = "24:00";
  } else {
    displayEnd = formatTime(eventEnd);
  }

  // Calculate minutes from midnight for positioning
  const startMinutes = spansFromPrevious
    ? 0
    : getMinutesFromMidnight(eventStart);
  const endMinutes = spansToNext
    ? 24 * MINUTES_PER_HOUR
    : getMinutesFromMidnight(eventEnd);

  return {
    displayStart,
    displayEnd,
    spansFromPrevious,
    spansToNext,
    isMultiDay,
    startMinutes,
    endMinutes,
    isStartDay: !spansFromPrevious && isSameDay(eventStart, day),
    isEndDay: !spansToNext && isSameDay(eventEnd, day),
  };
};

// Calculate event position with pixel-perfect accuracy
const calculateEventPosition = (displayInfo) => {
  if (!displayInfo) return { top: 0, height: 0 };

  const { startMinutes, endMinutes } = displayInfo;

  // Calculate the visible portion of the event within working hours
  const visibleStart = Math.max(startMinutes, WORKING_DAY_START_MINUTES);
  const visibleEnd = Math.min(endMinutes, WORKING_DAY_END_MINUTES);

  // If no visible portion (event is completely outside working hours)
  if (visibleStart >= visibleEnd) {
    return { top: 0, height: 0 };
  }

  // Calculate position in pixels (using 1px per minute ratio)
  const topPixels =
    (visibleStart - WORKING_DAY_START_MINUTES) * PIXELS_PER_MINUTE;
  const heightPixels = (visibleEnd - visibleStart) * PIXELS_PER_MINUTE;

  // Convert to CSS values with 2 decimal precision
  return {
    top: `${Math.round(topPixels * 100) / 100}px`,
    height: `${Math.max(Math.round(heightPixels * 100) / 100, 20)}px`, // Minimum 20px height
  };
};

// Check if a date is the first day of a multi-day event
const isFirstDayOfMultiDayEvent = (event, day) => {
  const eventStart = new Date(event.start);
  const dayDate = new Date(day);

  // Set both dates to start of day for comparison
  eventStart.setHours(0, 0, 0, 0);
  dayDate.setHours(0, 0, 0, 0);

  // Check if this is the first day of a multi-day event
  return event.isMultiDay && eventStart.getTime() === dayDate.getTime();
};

// Main Calendar Component
const CalendarTimeTable = () => {
  // Get context values including showAllBookings
  const { activeSpaceId, selectedPlaceGroupId, showAllBookings } =
    useOutletContext();
  const { t } = useTranslation("global");
  const { language } = useLanguage();

  // Map simple language codes to proper locale codes for date formatting
  const getLocaleForDate = useCallback(() => {
    const localeMap = {
      fr: "fr-FR",
      en: "en-US",
      uk: "en-US",
      es: "es-ES",
      be: "fr-BE",
      // Add more as needed
    };

    // If it's already a locale code (contains hyphen), use it directly
    if (language && language.includes("-")) {
      return language;
    }

    // Otherwise map it
    return localeMap[language] || "en-EN";
  }, [language]);

  const locale = getLocaleForDate();

  const [events, setEvents] = useState([]);
  const [multiDayEvents, setMultiDayEvents] = useState([]);
  const { openModal, closeModal } = useModal();
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentView, setCurrentView] = useState(VIEWS.MONTH);
  const [currentDate, setCurrentDate] = useState(() => new Date());
  const [isLoading, setIsLoading] = useState(false);
  const token = useSelector((state) => state.auth.token);
  const calendarRef = useRef(null);
  const weekContainerRef = useRef(null);
  const dayContainerRef = useRef(null);

  // Memoized services
  const privateBookingService = useMemo(
    () => createPrivateBookingService(token),
    [token]
  );

  // Utility functions
const formatDate = useCallback((date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}, []);

  const addDays = useCallback((date, days) => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  }, []);

  const getWeekStart = useCallback((date) => {
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1);
    const result = new Date(date);
    result.setDate(diff);
    return result;
  }, []);

  const getWeekDays = useCallback(
    (startDate) => {
      return Array.from({ length: 7 }).map((_, i) => addDays(startDate, i));
    },
    [addDays]
  );

  const getMonthDays = useCallback(
    (date) => {
      const year = date.getFullYear();
      const month = date.getMonth();
      const firstDay = new Date(year, month, 1);
      const lastDay = new Date(year, month + 1, 0);
      const startDate = getWeekStart(firstDay);
      const endDate = addDays(getWeekStart(lastDay), 6);

      const days = [];
      let current = new Date(startDate);
      while (current <= endDate) {
        days.push(new Date(current));
        current.setDate(current.getDate() + 1);
        current = new Date(current);
      }

      return days;
    },
    [getWeekStart, addDays]
  );

  // Process and consolidate multi-day events
  const processEvents = useCallback((events) => {
    const processedEvents = [];
    const processedMultiDayEvents = [];
    const eventMap = new Map();

    events.forEach((event) => {
      const start = new Date(event.start);
      const end = new Date(event.end);

      if (isMultiDayEvent(start, end)) {
        const eventKey = `${event.id}-${event.type}-${
          event.extendedProps?.customer?.email ||
          event.extendedProps?.place?.id ||
          "unknown"
        }`;

        if (!eventMap.has(eventKey)) {
          const multiDayEvent = {
            ...event,
            isMultiDay: true,
            spannedDays: [],
            originalEvents: [event],
          };
          eventMap.set(eventKey, multiDayEvent);
        } else {
          const existingEvent = eventMap.get(eventKey);
          existingEvent.originalEvents.push(event);

          if (start < new Date(existingEvent.start)) {
            existingEvent.start = start;
          }
          if (end > new Date(existingEvent.end)) {
            existingEvent.end = end;
          }
        }
      } else {
        processedEvents.push({
          ...event,
          isMultiDay: false,
        });
      }
    });

    eventMap.forEach((multiDayEvent) => {
      const startDate = new Date(multiDayEvent.start);
      const endDate = new Date(multiDayEvent.end);
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);

      const spannedDays = [];
      let currentDay = new Date(startDate);
      while (currentDay <= endDate) {
        spannedDays.push(new Date(currentDay));
        currentDay.setDate(currentDay.getDate() + 1);
        currentDay = new Date(currentDay);
      }

      multiDayEvent.spannedDays = spannedDays;
      processedMultiDayEvents.push(multiDayEvent);
    });

    return {
      singleDayEvents: processedEvents,
      multiDayEvents: processedMultiDayEvents,
    };
  }, []);

  // Format events from API data with proper timezone handling
  const formatEvents = useCallback(
    (data) => {
      if (!data) return [];

      const formattedEvents = [];

      // Helper function to create date with local timezone
      const createLocalDateTime = (dateStr, timeStr = "00:00") => {
        if (!dateStr) return new Date();

        // Parse date components
        const dateParts = dateStr.split("-");
        if (dateParts.length < 3) return new Date();

        const year = parseInt(dateParts[0], 10);
        const month = parseInt(dateParts[1], 10) - 1;
        const day = parseInt(dateParts[2], 10);

        // Parse time components if provided
        let hours = 0,
          minutes = 0;
        if (timeStr && timeStr !== "00:00") {
          const timeParts = timeStr.split(":");
          if (timeParts.length >= 2) {
            hours = parseInt(timeParts[0], 10);
            minutes = parseInt(timeParts[1], 10);
          }
        }

        // Create date in local timezone
        return new Date(year, month, day, hours, minutes, 0, 0);
      };

      // Process bookings
      (data.bookings || []).forEach((booking) => {
        try {
          const groupColor = booking.place?.placeGroup?.color;
          const eventColor = groupColor || DEFAULT_COLORS[EVENT_TYPES.BOOKING];

          let startDate, endDate;

          if (booking.start_date && booking.end_date) {
            // Handle dates with potential timezone issues
            if (booking.start_hour && booking.end_hour) {
              // Has separate time fields
              startDate = createLocalDateTime(
                booking.start_date,
                booking.start_hour
              );
              endDate = createLocalDateTime(
                booking.end_date,
                booking.end_hour
              );
            } else {
              // Parse ISO strings directly
              startDate = new Date(booking.start_date);
              endDate = new Date(booking.end_date);

              // Validate dates
              if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
                console.warn("Invalid date in booking:", booking);
                return;
              }
            }
          } else {
            // Fallback
            startDate = new Date();
            endDate = new Date();
          }

          // Store the FULL booking data in extendedProps
          formattedEvents.push({
            title: booking.place?.name || t("calendar.defaultEventTitle"),
            start: startDate,
            end: endDate,
            id: booking.id,
            type: EVENT_TYPES.BOOKING,
            color: eventColor,
            borderColor: eventColor,
            extendedProps: {
              capacity: booking.capacity,
              customer: booking.user
                ? {
                    firstName: booking.user.first_name,
                    lastName: booking.user.last_name,
                    email: booking.user.email,
                    phone: booking.user.phone,
                    company: booking.user.social_reason,
                  }
                : booking.customer_first_name
                ? {
                    firstName: booking.customer_first_name,
                    lastName: booking.customer_last_name,
                    email: booking.customer_email,
                    phone: booking.customer_phone,
                    company: booking.customer_social_reason,
                  }
                : null,
              place: booking.place,
              placeGroup: booking.place?.placeGroup,
              status: booking.status,
              priority: booking.priority,
              rawBooking: booking,
            },
          });
        } catch (error) {
          console.warn("Error formatting booking event:", error, booking);
        }
      });

      // Process private bookings
      (data.private_bookings || []).forEach((booking) => {
        try {
          const groupColor = booking.place?.placeGroup?.color;
          const eventColor = groupColor || DEFAULT_COLORS[EVENT_TYPES.PRIVATE];

          const startDate = createLocalDateTime(
            booking.start_date,
            booking.start_hour
          );
          const endDate = createLocalDateTime(
            booking.end_date,
            booking.end_hour
          );

          formattedEvents.push({
            title: booking.title,
            start: startDate,
            end: endDate,
            id: booking.id,
            type: EVENT_TYPES.PRIVATE,
            color: eventColor,
            borderColor: eventColor,
            extendedProps: {
              capacity: booking.capacity,
              services_equipments: booking.services_equipments,
              disposition_type: booking.disposition_type,
              customer: booking.user
                ? {
                    firstName: booking.user.first_name,
                    lastName: booking.user.last_name,
                    email: booking.user.email,
                    phone: booking.user.phone,
                    company: booking.user.social_reason,
                  }
                : booking.customer_first_name
                ? {
                    firstName: booking.customer_first_name,
                    lastName: booking.customer_last_name,
                    email: booking.customer_email,
                    phone: booking.customer_phone,
                    company: booking.customer_social_reason,
                  }
                : null,
              place: booking.place,
              placeGroup: booking.place?.placeGroup,
              staff: booking.staff,
              status: booking.status,
              priority: booking.priority,
              rawBooking: booking,
            },
          });
        } catch (error) {
          console.warn("Error formatting private booking event:", error, booking);
        }
      });

      // Process Google events
      (data.google_events || []).forEach((booking) => {
        try {
          const groupColor = booking.place?.placeGroup?.color;
          const eventColor = groupColor || DEFAULT_COLORS[EVENT_TYPES.GOOGLE];

          const startDate = createLocalDateTime(
            booking.start_date,
            booking.start_hour
          );
          const endDate = createLocalDateTime(
            booking.end_date,
            booking.end_hour
          );

          formattedEvents.push({
            title: booking.title,
            start: startDate,
            end: endDate,
            id: booking.id,
            type: EVENT_TYPES.GOOGLE,
            color: eventColor,
            borderColor: eventColor,
            extendedProps: {
              capacity: booking.capacity,
              customer: booking.user
                ? {
                    firstName: booking.user.first_name,
                    lastName: booking.user.last_name,
                    email: booking.user.email,
                    phone: booking.user.phone,
                    company: booking.user.social_reason,
                  }
                : booking.customer_first_name
                ? {
                    firstName: booking.customer_first_name,
                    lastName: booking.customer_last_name,
                    email: booking.customer_email,
                    phone: booking.customer_phone,
                    company: booking.customer_social_reason,
                  }
                : null,
              place: booking.place,
              placeGroup: booking.place?.placeGroup,
              staff: booking.staff,
              status: booking.status,
              priority: booking.priority,
              rawBooking: booking,
            },
          });
        } catch (error) {
          console.warn("Error formatting google event:", error, booking);
        }
      });

      return formattedEvents;
    },
    [t]
  );

  // Get events for a specific day with month view optimizations
  const getEventsForDay = useCallback(
    (day, forMonthView = false) => {
      const dayStart = new Date(day);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(day);
      dayEnd.setHours(23, 59, 59, 999);

      // Single day events
      const singleDayEvents = events
        .filter((event) => {
          const eventStart = new Date(event.start);
          const eventEnd = new Date(event.end);
          return eventStart <= dayEnd && eventEnd >= dayStart;
        })
        .map((event) => ({
          ...event,
          displayInfo: getEventDisplayInfo(event, day),
        }));

      // For month view, filter multi-day events to only show on first day
      let multiDayEventsForDay = [];

      if (forMonthView) {
        multiDayEventsForDay = multiDayEvents
          .filter((event) => {
            // Only include if this is the first day of the multi-day event
            return isFirstDayOfMultiDayEvent(event, day);
          })
          .map((event) => ({
            ...event,
            displayInfo: getEventDisplayInfo(event, day),
          }));
      } else {
        // For week/day view, show multi-day events on all days
        multiDayEventsForDay = multiDayEvents
          .filter((event) => {
            const eventStart = new Date(event.start);
            const eventEnd = new Date(event.end);
            return eventStart <= dayEnd && eventEnd >= dayStart;
          })
          .map((event) => ({
            ...event,
            displayInfo: getEventDisplayInfo(event, day),
          }));
      }

      // Combine all events and filter out null displayInfo
      const allEvents = [...singleDayEvents, ...multiDayEventsForDay].filter(
        (event) => event.displayInfo !== null
      );

      // Sort events: multi-day first, then by start time
      return allEvents.sort((a, b) => {
        // Multi-day events first
        if (a.isMultiDay && !b.isMultiDay) return -1;
        if (!a.isMultiDay && b.isMultiDay) return 1;

        // Then by start time
        const aTime = a.displayInfo?.isStartDay
          ? a.start
          : a.displayInfo?.spansFromPrevious
          ? new Date(0)
          : a.start;
        const bTime = b.displayInfo?.isStartDay
          ? b.start
          : b.displayInfo?.spansFromPrevious
          ? new Date(0)
          : b.start;
        return aTime - bTime;
      });
    },
    [events, multiDayEvents]
  );

  // Load events - UPDATED to handle showAllBookings
  const loadEvents = useCallback(async () => {
    try {
      setIsLoading(true);

      if (searchQuery) {
        try {
          const booking = await CalendarService.getBookingById(
            token,
            searchQuery
          );
          if (!booking) {
            setEvents([]);
            setMultiDayEvents([]);
            return;
          }
          const formattedEvents = formatEvents({
            bookings: [booking],
            private_bookings: [],
            google_events: [],
          });

          const processed = processEvents(formattedEvents);
          setEvents(processed.singleDayEvents);
          setMultiDayEvents(processed.multiDayEvents);
          return;
        } catch (searchError) {
          console.error("Search error:", searchError);
          setEvents([]);
          setMultiDayEvents([]);
          return;
        }
      }

      let startDate, endDate;
      if (currentView === VIEWS.DAY) {
        startDate = new Date(currentDate);
        endDate = new Date(currentDate);
        endDate.setDate(endDate.getDate() + 1); // Add one day to get a 2-day range
      } else if (currentView === VIEWS.WEEK) {
        startDate = getWeekStart(new Date(currentDate));
        endDate = addDays(startDate, 6);
      } else {
        startDate = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth(),
          1
        );
        endDate = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth() + 1,
          0
        );
      }

      const params = {
        start_date: formatDate(startDate),
        end_date: formatDate(endDate),
      };

      // CRITICAL: Booking filtering logic with showAllBookings
      if (showAllBookings) {
        // When "All Spaces" is selected, show ALL bookings
        // Don't add any place_id or place_group_id filters
        console.log("Showing all bookings across all spaces and groups");
      } else if (activeSpaceId) {
        // When a specific space is selected, show only bookings for that space
        params.place_id = activeSpaceId;
        console.log(`Filtering by specific space: ${activeSpaceId}`);
      } else if (
        selectedPlaceGroupId &&
        selectedPlaceGroupId !== "%7Bid%7D" &&
        selectedPlaceGroupId !== "all_spaces" &&
          selectedPlaceGroupId !== "{id}"

      ) {
        // When a specific group is selected, show only bookings from spaces in that group
        params.place_group_id = selectedPlaceGroupId;
        console.log(`Filtering by place group: ${selectedPlaceGroupId}`);
} else if (
  selectedPlaceGroupId === "%7Bid%7D" || 
  selectedPlaceGroupId === "{id}"
) {
  // When "No Group" is selected, show only bookings from spaces without groups
  // Use the raw value so URLSearchParams encodes it properly
  // params.place_group_id = "{id}";
  console.log("Filtering by spaces without groups");
}
      // If no filters are applied (should not happen normally), show all bookings

      const calendarData = await CalendarService.getCalendarEvents(
        token,
        params
      );
      const formattedEvents = formatEvents(calendarData);

      const processed = processEvents(formattedEvents);
      setEvents(processed.singleDayEvents);
      setMultiDayEvents(processed.multiDayEvents);
    } catch (error) {
      console.error("Error loading events:", error);
      setEvents([]);
      setMultiDayEvents([]);
    } finally {
      setIsLoading(false);
    }
  }, [
    currentView,
    currentDate,
    activeSpaceId,
    selectedPlaceGroupId,
    showAllBookings,
    searchQuery,
    token,
    formatDate,
    getWeekStart,
    addDays,
    formatEvents,
    processEvents,
  ]);

  // Booking submission handler
  const handleSubmitBooking = useCallback(
    async (bookingData, sendMail = false) => {
      try {
        setIsLoading(true);
        const response = bookingData.id
          ? await privateBookingService.updatePrivateBooking(
              bookingData.id,
              bookingData,
              sendMail
            )
          : await privateBookingService.createPrivateBooking(
              bookingData,
              sendMail
            );

        if (response.success) {
          closeModal();
          setTimeout(() => {
            openModal({
              content: <p>{response.message}</p>,
              title: null,
              type: "success",
              autoClose: true,
              closeDelay: 1500,
            });
          }, 300);
          loadEvents();
          return { success: true };
        }

        return {
          success: false,
          error: response.error?.message || t("calendar.bookingFailed"),
        };
      } catch (error) {
        return {
          success: false,
          error: error.message || t("calendar.unexpectedError"),
        };
      } finally {
        setIsLoading(false);
      }
    },
    [privateBookingService, closeModal, openModal, loadEvents, t]
  );

  // Event handlers
const openAddBookingModal = useCallback(
  (date = new Date(), bookingToDuplicate = null) => {
    let initialBooking = null;
    
    if (bookingToDuplicate) {
      // Extract data from the event to duplicate
      const startDate = date || bookingToDuplicate.start || new Date();
      const endDate = date ? new Date(date) : new Date(bookingToDuplicate.end || new Date());
      
      // If we have a date, set the end time to be 1 hour later by default
      if (date && !bookingToDuplicate.end) {
        endDate.setHours(endDate.getHours() + 1);
      }
      
      // Get the place with proper structure for SelectInput
      const place = bookingToDuplicate.extendedProps?.place ? {
        id: bookingToDuplicate.extendedProps.place.id,
        name: bookingToDuplicate.extendedProps.place.name,
        label: bookingToDuplicate.extendedProps.place.name,
        value: bookingToDuplicate.extendedProps.place.id,
        placeUsage: bookingToDuplicate.extendedProps.place.placeUsage,
        minimal_duration: bookingToDuplicate.extendedProps.place.minimal_duration,
        placeGroup: bookingToDuplicate.extendedProps.place.placeGroup || null,
        ...bookingToDuplicate.extendedProps.place
      } : null;
      
      // Get staff with proper structure for SelectInput
      const staff = bookingToDuplicate.extendedProps?.staff ? {
        id: bookingToDuplicate.extendedProps.staff.id,
        name: bookingToDuplicate.extendedProps.staff.name,
        label: bookingToDuplicate.extendedProps.staff.name,
        value: bookingToDuplicate.extendedProps.staff.id,
        ...bookingToDuplicate.extendedProps.staff
      } : null;
      
      // Get group filter with proper structure for SelectInput
      const groupFilter = place?.placeGroup ? {
        id: place.placeGroup.id,
        label: place.placeGroup.label || place.placeGroup.name || "Group",
        value: place.placeGroup.id,
        ...place.placeGroup
      } : null;
      
      initialBooking = {
        id: bookingToDuplicate.id,
        title: bookingToDuplicate.title || "",
        place: place,
        staff: staff,
        groupFilter: groupFilter,
        customer_social_reason: bookingToDuplicate.extendedProps?.customer?.company || "",
        customer_first_name: bookingToDuplicate.extendedProps?.customer?.firstName || "",
        customer_last_name: bookingToDuplicate.extendedProps?.customer?.lastName || "",
        customer_email: bookingToDuplicate.extendedProps?.customer?.email || "",
        customer_phone: bookingToDuplicate.extendedProps?.customer?.phone || "",
        services_equipments: bookingToDuplicate.extendedProps?.services_equipments || "",
        capacity: bookingToDuplicate.extendedProps?.capacity || 1,
        disposition_type: bookingToDuplicate.extendedProps?.disposition_type || "",
        // Date objects for the modal
        start_date_dt: startDate,
        end_date_dt: endDate,
        start_hour_dt: formatTime(startDate),
        end_hour_dt: formatTime(endDate),
      };
    }
    
    openModal({
      size: "medium",
      title: bookingToDuplicate
        ? t("modal.duplicateBooking.title")
        : t("modal.newbooking.title"),
      content: (
        <AddNewBookingModal
          initialBooking={initialBooking}
          onClose={closeModal}
          onSubmit={handleSubmitBooking}
        />
      ),
    });
  },
  [openModal, t, handleSubmitBooking, closeModal]
);

  // Handle date selection from toolbar date picker
  const handleDateSelect = useCallback(
    (selectedDate) => {
      setCurrentDate(selectedDate);
    },
    []
  );

  // Handle time slot click for creating new bookings
  const handleTimeSlotClick = useCallback(
    (day, hour = null, minute = 0, clientY = null, slotElement = null) => {
      let clickedDate = new Date(day);

      if (hour !== null) {
        // For Week and Day views - precise time selection
        let calculatedMinute = minute;

        // If we have click position data, calculate exact minute
        if (clientY && slotElement) {
          const rect = slotElement.getBoundingClientRect();
          const clickPosition = clientY - rect.top;
          const slotHeight = rect.height;
          const percentage = clickPosition / slotHeight;
          calculatedMinute = Math.floor(percentage * 60);
          // Round to nearest 15 minutes for better UX
          calculatedMinute = Math.round(calculatedMinute / 15) * 15;
          // Clamp to valid range
          calculatedMinute = Math.max(0, Math.min(calculatedMinute, 45));
        }

        clickedDate.setHours(hour, calculatedMinute, 0, 0);
      } else {
        // For Month view - use default time (e.g., 9:00 AM)
        const defaultHour = WORKING_DAY_START_HOUR + 1; // 9:00 AM as default
        clickedDate.setHours(defaultHour, 0, 0, 0);
      }

      openAddBookingModal(clickedDate);
    },
    [openAddBookingModal]
  );

  const handleEventClick = useCallback(
    (event) => {
      setSelectedEvent(event);

      switch (event.type) {
        case EVENT_TYPES.PRIVATE:
          openModal({
            size: "medium",
            content: (
              <PrivateBookingDetailsModal
                event={event}
                onClose={closeModal}
                onDeleteSuccess={loadEvents}
                onSubmit={handleSubmitBooking}
              />
            ),
          });
          break;
        case EVENT_TYPES.BOOKING:
          const rawBookingData = event.extendedProps?.rawBooking || event;

          openModal({
            size: "medium",
            content: (
              <CustomBookingDetailsModal
                booking={rawBookingData}
                onClose={closeModal}
                onDeleteSuccess={loadEvents}
                onDuplicate={() => {
                  closeModal();
                  openAddBookingModal(event.start, event);
                }}
                onApprove={async (bookingId) => {
                  // Your approval logic here
                }}
                onReject={async (bookingId) => {
                  // Your rejection logic here
                }}
              />
            ),
          });
          break;
        default:
          openModal({
            size: "medium",
            content: (
              <CalendarEventDetailsModal
                event={event}
                onClose={closeModal}
                onDuplicate={() => {
                  closeModal();
                  openAddBookingModal(event.start, event);
                }}
              />
            ),
          });
      }
    },
    [openModal, closeModal, loadEvents, handleSubmitBooking, openAddBookingModal]
  );

  const handlePrev = useCallback(() => {
    const newDate = new Date(currentDate);
    switch (currentView) {
      case VIEWS.DAY:
        newDate.setDate(newDate.getDate() - 1);
        break;
      case VIEWS.WEEK:
        newDate.setDate(newDate.getDate() - 7);
        break;
      case VIEWS.MONTH:
        newDate.setMonth(newDate.getMonth() - 1);
        break;
    }
    setCurrentDate(newDate);
  }, [currentDate, currentView]);

  const handleNext = useCallback(() => {
    const newDate = new Date(currentDate);
    switch (currentView) {
      case VIEWS.DAY:
        newDate.setDate(newDate.getDate() + 1);
        break;
      case VIEWS.WEEK:
        newDate.setDate(newDate.getDate() + 7);
        break;
      case VIEWS.MONTH:
        newDate.setMonth(newDate.getMonth() + 1);
        break;
    }
    setCurrentDate(newDate);
  }, [currentDate, currentView]);

  const handleToday = useCallback(() => {
    setCurrentDate(new Date());
  }, []);

  const handleDayClick = useCallback((day) => {
    setCurrentDate(day);
    setCurrentView(VIEWS.DAY);
  }, []);

  // Render Month View
  const renderMonthView = useMemo(() => {
    const days = getMonthDays(currentDate);
    const weeks = [];

    for (let i = 0; i < days.length; i += 7) {
      weeks.push(days.slice(i, i + 7));
    }

    // Get weekday names in the current locale
    const weekdayNames = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(Date.UTC(2023, 0, 2 + i)); // Start from Monday
      const weekday = date.toLocaleDateString(locale, {
        weekday: "short",
        timeZone: "UTC",
      });
      weekdayNames.push(weekday);
    }

    return (
      <div className={styles.monthView}>
        <div className={styles.monthHeader}>
          {weekdayNames.map((day, index) => (
            <div key={`weekday-${index}`} className={styles.weekdayHeader}>
              {day}
            </div>
          ))}
        </div>

        <div className={styles.monthGrid}>
          {weeks.map((week, weekIndex) => (
            <div key={`week-${weekIndex}`} className={styles.weekRow}>
              {week.map((day) => {
                const isCurrentMonth = day.getMonth() === currentDate.getMonth();
                const isToday = isSameDay(day, new Date());

                // Get events for day with month view optimizations
                const dayEvents = getEventsForDay(day, true);
                const visibleEvents = dayEvents.slice(0, 3);

                return (
                  <div
                    key={`day-${day.getTime()}`}
                    className={`
                      ${styles.dayCell}
                      ${isCurrentMonth ? styles.currentMonth : styles.otherMonth}
                      ${isToday ? styles.today : ""}
                    `}
                    onClick={() => !isLoading && handleTimeSlotClick(day)}
                  >
                    <div className={styles.dayNumber}>{day.getDate()}</div>

                    <div className={styles.dayEvents}>
                      {!isLoading ? (
                        <>
                          {visibleEvents.map((event, index) => (
                            <CalendarEvent
                              key={`${event.id}-${index}-${day.getTime()}`}
                              event={event}
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                handleEventClick(event);
                              }}
                              displayInfo={event.displayInfo}
                              isMultiDay={event.isMultiDay}
                              isMonthView={true}
                              eventIndex={index}
                            />
                          ))}

                          {dayEvents.length > 3 && (
                            <div
                              className={styles.moreEvents}
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                handleDayClick(day);
                              }}
                            >
                              +{dayEvents.length - 3} more
                            </div>
                          )}
                        </>
                      ) : (
                        <div
                          className={`${styles.skeleton} ${styles.skeletonEvent}`}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  }, [
    currentDate,
    locale,
    isLoading,
    handleDayClick,
    handleEventClick,
    handleTimeSlotClick,
    getMonthDays,
    getEventsForDay,
  ]);

  // Render Week View
  const renderWeekView = useMemo(() => {
    const weekStart = getWeekStart(new Date(currentDate));
    const days = getWeekDays(weekStart);

    return (
      <div className={styles.weekView} ref={weekContainerRef}>
        <div className={styles.weekHeader}>
          {/* Empty cell for time column header */}
          <div className={styles.timeColumnHeader}></div>

          {/* Day headers */}
          {days.map((day, index) => {
            const isToday = isSameDay(day, new Date());
            return (
              <div
                key={`day-header-${index}`}
                className={`${styles.dayHeaderCell} ${
                  isToday ? styles.today : ""
                }`}
              >
                <div className={styles.dayName}>
                  {day.toLocaleDateString(locale, { weekday: "short" })}
                </div>
                <div className={styles.dateNumber}>{day.getDate()}</div>
              </div>
            );
          })}
        </div>

        <div className={styles.weekContent}>
          {/* Time column */}
          <div className={styles.timeColumn}>
            {WORKING_HOURS.map((hour) => (
              <div key={`hour-${hour}`} className={styles.timeSlotLabel}>
                {hour}:00
              </div>
            ))}
          </div>

          {/* Days columns - scrollable area */}
          <div className={styles.daysContainer}>
            {days.map((day, dayIndex) => {
              const eventsForDay = getEventsForDay(day, false);

              return (
                <div key={`day-column-${dayIndex}`} className={styles.dayColumn}>
                  <div
                    className={styles.dayEventsWrapper}
                    style={{
                      height: `${TOTAL_VISIBLE_HEIGHT}px`,
                      boxSizing: "border-box",
                      position: "relative",
                    }}
                  >
                    {/* Time slots background for visual reference */}
                    {WORKING_HOURS.map((hour, hourIndex) => (
                      <div
                        key={`slot-${dayIndex}-${hour}`}
                        className={styles.hourSlot}
                        style={{
                          height: `${HOUR_SLOT_HEIGHT}px`,
                          position: "relative",
                          cursor: "pointer",
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleTimeSlotClick(
                            day,
                            hour,
                            0,
                            e.clientY,
                            e.currentTarget
                          );
                        }}
                      >
                        <div className={styles.hourSlotHover} />
                      </div>
                    ))}

                    {eventsForDay.map((event, eventIndex) => {
                      const position = calculateEventPosition(
                        event.displayInfo
                      );

                      const style = {
                        top: position.top,
                        height: position.height,
                        position: "absolute",
                        left: "2px",
                        right: "2px",
                        zIndex: 10 + eventIndex,
                        boxSizing: "border-box",
                      };

                      return (
                        <CalendarEvent
                          key={`${event.id}-${dayIndex}-${eventIndex}`}
                          event={event}
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleEventClick(event);
                          }}
                          style={style}
                          displayInfo={event.displayInfo}
                          isMultiDay={event.isMultiDay}
                        />
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }, [
    currentDate,
    locale,
    getEventsForDay,
    handleEventClick,
    handleTimeSlotClick,
    getWeekStart,
    getWeekDays,
  ]);

  // Render Day View
  const renderDayView = useMemo(() => {
    const day = currentDate;

    const eventsForDay = getEventsForDay(day, false);

    return (
      <div className={styles.dayView} ref={dayContainerRef}>
        {/* Fixed header - separate from scrollable content */}
        <div className={styles.dayViewHeader}>
          <div className={styles.dayHeaderInfo}>
            <div className={styles.dayName}>
              {day.toLocaleDateString(locale, { weekday: "long" })}
            </div>
            <div className={styles.dateNumber}>
              {day.toLocaleDateString(locale, {
                month: "long",
                day: "numeric",
                year: "numeric",
              })}
            </div>
          </div>
        </div>

        {/* Scrollable content area */}
        <div className={styles.dayContent}>
          {/* Time column */}
          <div className={styles.timeColumn}>
            {WORKING_HOURS.map((hour) => (
              <div key={`hour-${hour}`} className={styles.timeSlotLabel}>
                {hour}:00
              </div>
            ))}
          </div>

          {/* Events column */}
          <div className={styles.eventsColumn}>
            <div
              className={styles.eventsWrapper}
              style={{
                height: `${TOTAL_VISIBLE_HEIGHT}px`,
                boxSizing: "border-box",
                position: "relative",
              }}
            >
              {/* Time slots background with click handlers */}
              {WORKING_HOURS.map((hour, hourIndex) => (
                <div
                  key={`slot-${hour}`}
                  className={styles.hourSlot}
                  style={{
                    height: `${HOUR_SLOT_HEIGHT}px`,
                    position: "relative",
                    cursor: "pointer",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTimeSlotClick(
                      day,
                      hour,
                      0,
                      e.clientY,
                      e.currentTarget
                    );
                  }}
                >
                  <div className={styles.hourSlotHover} />
                </div>
              ))}

              {eventsForDay.map((event, index) => {
                const position = calculateEventPosition(event.displayInfo);

                const style = {
                  top: position.top,
                  height: position.height,
                  position: "absolute",
                  left: "2px",
                  right: "2px",
                  zIndex: 10 + index,
                  boxSizing: "border-box",
                };

                return (
                  <CalendarEvent
                    key={`${event.id}-${index}`}
                    event={event}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      handleEventClick(event);
                    }}
                    style={style}
                    displayInfo={event.displayInfo}
                    isMultiDay={event.isMultiDay}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }, [currentDate, locale, getEventsForDay, handleEventClick, handleTimeSlotClick]);

  // Prepare toolbar props
  const toolbarProps = useMemo(
    () => ({
      locale,
      currentView,
      currentDate,
      isLoading,
      searchQuery,
      onPrev: handlePrev,
      onNext: handleNext,
      onToday: handleToday,
      onViewChange: setCurrentView,
      onSearchChange: setSearchQuery,
      onRefresh: loadEvents,
      onPrint: () => window.print(),
      onCreateBooking: () => openAddBookingModal(),
      onDateSelect: handleDateSelect, // Add date selector handler
      getWeekStart,
      addDays,
    }),
    [
      locale,
      currentView,
      currentDate,
      isLoading,
      searchQuery,
      handlePrev,
      handleNext,
      handleToday,
      setCurrentView,
      loadEvents,
      openAddBookingModal,
      handleDateSelect,
      getWeekStart,
      addDays,
    ]
  );

  // Effects
  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  useEffect(() => {
    let isMounted = true;

    const refreshEvents = () => {
      if (isMounted && !isLoading) {
        loadEvents();
      }
    };

    const interval = setInterval(refreshEvents, 5 * 60 * 1000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [isLoading, loadEvents]);

  return (
    <div className={styles.container} ref={calendarRef}>
      <CalendarToolbar {...toolbarProps} />

      {/* Calendar Body */}
      <div className={styles.calendarBody}>
        {isLoading && (
          <div className={styles.loadingOverlay}>
            <div className={styles.spinner} />
          </div>
        )}

        {currentView === VIEWS.MONTH && renderMonthView}
        {currentView === VIEWS.WEEK && renderWeekView}
        {currentView === VIEWS.DAY && renderDayView}

        {!isLoading &&
          events.length === 0 &&
          multiDayEvents.length === 0 &&
          !searchQuery && (
            <div className={styles.emptyState}>
              <CalendarIcon size={48} />
              <h3>{t("calendar.noEvents")}</h3>
              <p>{t("calendar.noEventsDescription")}</p>
            </div>
          )}
      </div>
    </div>
  );
};

export default memo(CalendarTimeTable);